"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { BookOpen, LayoutGrid, LogOut, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PrefToggles } from "@/components/prefs/pref-toggles";
import { usePrefs } from "@/components/providers/app-providers";
import { cn } from "@/lib/utils";

export function AppHeader({ userEmail }: { userEmail?: string }) {
  const { t } = usePrefs();
  const pathname = usePathname();

  const nav = [
    { href: "/dashboard", label: t("navDevices"), icon: LayoutGrid },
    { href: "/api", label: t("apiDocsLink"), icon: BookOpen },
  ];

  async function logoutUser() {
    await fetch("/api/auth/logout", { method: "POST" });
    window.location.href = "/login";
  }

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/80 backdrop-blur-md dark:border-slate-800 dark:bg-slate-950/80">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-3 px-4 sm:px-6">
        <Link href="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-white shadow-sm">
            <MessageCircle className="h-4 w-4" />
          </span>
          <span className="hidden font-[family-name:var(--font-manrope)] text-lg font-bold tracking-tight text-slate-950 min-[400px]:inline dark:text-white">
            {t("brand")}
          </span>
        </Link>

        <nav className="ms-1 flex items-center gap-1 sm:ms-6">
          {nav.map(({ href, label, icon: Icon }) => {
            const active = pathname === href;
            return (
              <Link
                key={href}
                href={href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex h-9 items-center gap-2 rounded-lg px-2.5 text-sm font-medium transition-colors sm:px-3",
                  active
                    ? "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white",
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="ms-auto flex items-center gap-1 sm:gap-2">
          <PrefToggles />
          {userEmail && (
            <>
              <span className="mx-1 hidden h-6 w-px bg-slate-200 dark:bg-slate-800 md:block" />
              <div className="hidden min-w-0 flex-col md:flex">
                <span className="text-[11px] leading-tight text-slate-500 dark:text-slate-400">
                  {t("signedInAs")}
                </span>
                <span
                  className="max-w-[12rem] truncate text-sm font-medium leading-tight text-slate-800 dark:text-slate-100"
                  dir="ltr"
                  title={userEmail}
                >
                  {userEmail}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void logoutUser()}
                aria-label={t("logout")}
                title={t("logout")}
              >
                <LogOut className="h-4 w-4 rtl:-scale-x-100" />
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
